export type OnboardingStepCommand = {
  command: string;
  description: string;
};

export type OnboardingStepAction = {
  label: string;
  href?: string;
  /** If true, this action navigates to an in-app route (use React Router) */
  isRoute?: boolean;
  /** If set, the href is resolved dynamically at render time instead of using `href`. */
  dynamicRoute?: "agent-overview" | "agent-function-debugger" | "agent-sessions-trace" | "agent-releases";
};

export type OnboardingStepDef = {
  id: string;
  /** Checklist title — shown in the collapsed row */
  title: string;
  /** Panel description — shown when expanded */
  description: string;
  /** CLI commands to display with copy buttons */
  commands: OnboardingStepCommand[];
  /** Button label shown to manually mark the step complete */
  manualButtonLabel: string;
  /** Action buttons/links shown in the expanded step */
  actions: OnboardingStepAction[];
};

export const ONBOARDING_STEPS: OnboardingStepDef[] = [
  {
    id: "step1",
    title: "Sign in to the CLI",
    description: "We use a CLI to push code and manage agents. Run `vr signin` in your terminal — it opens a browser tab to authorize.",
    commands: [
      {
        command: "vr signin",
        description: "Opens a browser tab to confirm. After you click confirm, your terminal is signed in.",
      },
    ],
    manualButtonLabel: "I've signed in",
    actions: [],
  },
  {
    id: "step2",
    title: "Scaffold your first agent",
    description: "Run vr init and pick the simple-agent template. Edit the handler if you want; or vr push it as-is.",
    commands: [
      {
        command: "vr init my-agent",
        description:
          "Creates a folder with a handler.py, requirements.txt, and .voicerun/ config. Pick the simple-agent template when prompted — it's an LLM-powered assistant you can have a conversation with out of the box.",
      },
    ],
    manualButtonLabel: "I've scaffolded my agent",
    actions: [],
  },
  {
    id: "step3",
    title: "Deploy it",
    description: "Push the code to VoiceRun, then release it to your dev environment. Two commands.",
    commands: [
      {
        command: "vr push",
        description: "Uploads your agent's code.",
      },
      {
        command: "vr release dev",
        description:
          "Binds the code to your `dev` environment so it's callable. A release in VoiceRun is the combination of an agent, a function, and an environment — think of it like a GitHub Deployment.",
      },
    ],
    manualButtonLabel: "I've deployed",
    actions: [],
  },
  {
    id: "step4",
    title: "See your agent in the dashboard",
    description: "Now that your agent is deployed, take a look at it here.",
    commands: [],
    manualButtonLabel: "I've seen it",
    actions: [
      {
        label: "Open my agent",
        isRoute: true,
        dynamicRoute: "agent-overview",
      },
    ],
  },
  {
    id: "step5",
    title: "Call your agent",
    description: "Have a conversation with what you just deployed. Two ways:",
    commands: [
      {
        command: "vr debug",
        description: "Opens a debug session you can talk to from the terminal. Hang up when done.",
      },
    ],
    manualButtonLabel: "I've called my agent",
    actions: [
      {
        label: "Try in browser",
        isRoute: true,
        dynamicRoute: "agent-function-debugger",
      },
    ],
  },
];

export const ONBOARDING_PHASE_2_STEPS: OnboardingStepDef[] = [
  {
    id: "step6",
    title: "View your first trace",
    description: "Open the Sessions tab on any agent and click a session to see the transcript, events, and trace.",
    commands: [],
    manualButtonLabel: "I've viewed a trace",
    actions: [
      {
        label: "View trace",
        isRoute: true,
        dynamicRoute: "agent-sessions-trace",
      },
    ],
  },
  {
    id: "step10",
    title: "Deploy to production",
    description: "Release your agent to a production environment so it can handle real traffic.",
    commands: [
      {
        command: "vr release production",
        description: "Releases the latest pushed code to your production environment.",
      },
    ],
    manualButtonLabel: "I've deployed to production",
    actions: [
      {
        label: "Open releases",
        isRoute: true,
        dynamicRoute: "agent-releases",
      },
    ],
  },
];

export const ALL_ONBOARDING_STEPS: OnboardingStepDef[] = [...ONBOARDING_STEPS, ...ONBOARDING_PHASE_2_STEPS];

export const PHASE_1_LABEL = "Get your first agent live";
export const PHASE_2_LABEL = "Learn the platform and tools";
export const PANEL_TITLE = "Getting started";
export const PANEL_SUBTITLE = "A quick tour of VoiceRun. About 10 minutes if you don't get distracted.";
